import React, { useCallback, useEffect, useState } from "react";
import { usePubNub } from "pubnub-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { COLORS } from "../../constants/colors";
import ChatInterface from "../chat/ChatInterface";
import ChatInfo from "../chat/ChatInfo";
import { sendMessage, getGroupMessages, leaveGroup } from "../../services/groupchatService";

const MyGroupChat = ({ chats, onBack = () => {} }) => {
  const userId = useSelector((state) => state.auth.id);
  const userType = useSelector((state) => state.auth.type);
  const auth = useSelector((state) => state.auth);
  const userProfile = useSelector((state) => state.user);
  const mentorProfile = useSelector((state) => state.mentor);
  
  // Get names and photo based on user type
  const firstName = userType === 'mentor' 
    ? mentorProfile.firstName || auth.firstName || ""
    : userProfile.firstName || auth.firstName || "";
  const lastName = userType === 'mentor'
    ? mentorProfile.lastName || auth.lastName || ""
    : userProfile.lastName || auth.lastName || "";
  const profilePhoto = userType === 'mentor'
    ? mentorProfile.profileImage
    : userProfile.profilePhoto;

    console.log(firstName, lastName, profilePhoto)
  const pubnub = usePubNub();
  const queryClient = useQueryClient();

  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Auto-select first chat
  useEffect(() => {
    if (chats?.length > 0 && !currentChat) {
      handleChatSelect(chats[0]);
    }
  }, [chats, currentChat]);

  // PubNub message handling
  const handleNewMessage = useCallback((event) => {
    const { text, sender, senderName, senderPhoto } = event.message;
    
    if (sender === userId) return; // Skip own messages
    
    setMessages(prev => {
      // Check for duplicates
      const exists = prev.some(msg => 
        msg.text === text && msg.sender === sender && 
        Math.abs(new Date(msg.timestamp) - new Date(event.timetoken / 10000)) < 5000
      );
      if (exists) return prev;
      
      // Remove optimistic messages
      const filtered = prev.filter(msg => 
        !(msg.id.startsWith('optimistic-') && msg.text === text && msg.sender === sender)
      );
      
      return [...filtered, {
        id: event.timetoken,
        text,
        sender,
        senderName: senderName || "Unknown User",
        senderPhoto,
        timestamp: new Date(event.timetoken / 10000),
        sent: false,
        showSenderInfo: true,
      }];
    });
  }, [userId]);

  // PubNub subscription
  useEffect(() => {
    if (!currentChat?.name) return;
    
    pubnub.subscribe({ channels: [currentChat.name] });
    pubnub.addListener({ message: handleNewMessage });
    
    return () => pubnub.unsubscribeAll();
  }, [currentChat?.name, handleNewMessage, pubnub]);

  // Send message
  const handleSendMessage = async (text) => {
    if (!currentChat) return;

    const senderName = `${firstName} ${lastName}`.trim() || "User";
    const optimisticMessage = {
      id: `optimistic-${Date.now()}`,
      text,
      sender: userId,
      senderName,
      senderPhoto: profilePhoto,
      timestamp: new Date(),
      sent: true,
      showSenderInfo: false,
    };

    setMessages(prev => [...prev, optimisticMessage]);

    try {
      // PubNub broadcast
      await pubnub.publish({
        channel: currentChat.name,
        message: { text, sender: userId, senderName: `${firstName} ${lastName}`.trim() || "User", senderPhoto: profilePhoto }
      });

      // Database save
      await sendMessage(currentChat._id, { sender: userId, content: text });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
    }
  };

  // Select chat and load messages
  const handleChatSelect = async (chat) => {
    setCurrentChat(chat);
    setIsLoadingMessages(true);
    setMessages([]);
    
    try {
      const groupMessages = await getGroupMessages(chat._id, userId);
      if (groupMessages?.data?.messages) {
        const formattedMessages = groupMessages.data.messages.map(msg => {
          const isOwn = msg.sender?.toString() === userId;
          
          return {
            id: msg.timestamp || Date.now(),
            text: msg.content,
            sender: msg.sender,
            senderName: isOwn 
              ? `${firstName} ${lastName}`.trim() || "You"
              : msg.senderName || "Unknown User",
            senderPhoto: isOwn ? profilePhoto : (msg.senderPhoto || ""),
            timestamp: new Date(msg.timestamp),
            sent: isOwn,
            showSenderInfo: !isOwn,
          };
        });
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Leave group
  const handleLeaveChannel = async () => {
    try {
      await leaveGroup(currentChat._id, userId);
      queryClient.setQueryData(["myGroups", userId], (old = []) => 
        old.filter(g => g._id !== currentChat._id)
      );
      setCurrentChat(null);
      setMessages([]);
      toast.success(`You left ${currentChat?.name}`);
    } catch (error) {
      console.error("Error leaving chat:", error);
      toast.error("Failed to leave channel");
    }
  };

  // Render group item
  const renderGroupItem = (item, isSelected) => {
    const lastMessage = item.lastMessage || "No messages yet";
    const senderName = item.lastMessageSender 
      ? `${item.lastMessageSender.firstName || ''} ${item.lastMessageSender.lastName || ''}`.trim()
      : null;

    return (
      <div
        key={item._id}
        onClick={() => handleChatSelect(item)}
        className={`p-3 rounded-lg border cursor-pointer transition-all ${
          isSelected ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 hover:border-gray-300'
        }`}
        style={isSelected ? { borderColor: COLORS.purple, backgroundColor: COLORS.purpleLight } : {}}
      >
        <h3 className="font-semibold text-gray-900 text-sm">{item.name || "Unnamed Group"}</h3>
        <p className="text-gray-500 text-xs mt-1">{item.participants?.length || 0} members</p>
        <p className="text-gray-600 text-xs mt-1 line-clamp-2">
          {lastMessage === "No messages yet" 
            ? "No messages yet"
            : senderName ? `${senderName}: ${lastMessage.substring(0, 45)}${lastMessage.length > 45 ? '...' : ''}`
            : lastMessage.substring(0, 45) + (lastMessage.length > 45 ? '...' : '')
          }
        </p>
      </div>
    );
  };

  // Render header
  const renderHeader = () => (
    <div className="p-4 border-b border-gray-300 flex justify-between items-center">
      <h2 className="text-lg font-bold text-gray-900">{currentChat?.name || "Unnamed Group"}</h2>
      <button
        onClick={() => setShowGroupInfo(true)}
        className="p-2 rounded-full hover:bg-gray-100 text-gray-600 focus:outline-none transition-colors"
        style={{ '--tw-ring-color': COLORS.purple }}
      >
        ℹ️
      </button>
    </div>
  );

  return (
    <>
      <ChatInterface
        items={chats || []}
        selectedItem={currentChat}
        messages={messages}
        isLoadingMessages={isLoadingMessages}
        title="My Groups"
        leftColumnTitle="Joined Groups"
        rightColumnTitle="Group Chat"
        emptyStateTitle="No messages yet"
        emptyStateSubtitle="Send a message to start the conversation with your group"
        sidebarEmptyStateSubtitle="Your joined groups will appear here"
        inputPlaceholder="Share your thoughts with the group..."
        onItemSelect={handleChatSelect}
        onSendMessage={handleSendMessage}
        selectedItemKey="_id"
        showBackButton
        onBack={onBack}
        showInfoButton
        onInfoClick={() => setShowGroupInfo(true)}
        renderItem={renderGroupItem}
        renderHeader={renderHeader}
        isGroupChat={true}
      />

      {showGroupInfo && currentChat && (
        <ChatInfo
          isOpen={showGroupInfo}
          onClose={() => setShowGroupInfo(false)}
          chat={currentChat}
          onLeave={() => {
            handleLeaveChannel();
            setShowGroupInfo(false);
          }}
        />
      )}
    </>
  );
};

export default MyGroupChat;